import React, { useCallback, useEffect, useMemo } from 'react';

import { useNavigate } from 'react-router';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Checkbox, FormControlLabel, Grid, Tooltip, Typography } from '@mui/material';
import { FieldOwnerState, LocalizationProvider, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { ptBR } from 'date-fns/locale';
import { Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { DeviceFieldFormik, ModuleFieldFormik } from '..';

import { CissTextField } from 'components/fields/';
import { Row } from 'components/form';
import { Block, FormPage } from 'components/page';
import { SubTitle } from 'components/text';
import { Upload } from 'components/upload';
import { useFormContext } from 'context/FormContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import useNavigateBack from 'hooks/useNavigateBack';
import { useFormMutation } from 'mutations/useFormMutation';
import { initialBannerValues, RequestBannerModelProps } from 'pages/banner/types';

export function BannerFormComp() {
    const { setLoading, setContent, content } = useFormContext();

    const url = '/gestao/banner';

    const { mutate: mutateBanner, isLoading } = useFormMutation();
    const { hasPermission } = usePermissionsContext();
    const navigateBack = useNavigateBack();
    const uploadAcceptExtensions = ['.jpg', '.jpeg', '.png', '.tiff'];

    const formatPayloadBannerModulos = (bannerModulos: any): any => {
        return bannerModulos?.map((modulo: any) => ({ modulo }));
    };

    const getMinDate = (): any => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    const handleSubmit = (values: RequestBannerModelProps, formik: FormikHelpers<RequestBannerModelProps>): void => {
        const modifyBannerModulos = formatPayloadBannerModulos(values?.bannerModulos);

        values.bannerModulos = modifyBannerModulos;

        mutateBanner(
            {
                url,
                idField: 'idBanner',
                formData: values,
                formik,
                preventRedirect: !hasPermission(['BANNER_ALTERAR']),
            },
            {
                onSuccess: (response: any) => {
                    const { data } = response.data;

                    if (!hasPermission(['BANNER_ALTERAR'])) {
                        navigateBack();
                    }

                    if (data) {
                        setContent(data);
                    }
                },
            },
        );
    };

    const imagePropsYupValidation = () => {
        return Yup.array(
            Yup.object().shape({
                arquivo: Yup.object({
                    dsArquivo: Yup.string(),
                    dsExtensao: Yup.string(),
                    dsHash: Yup.string(),
                    dsLink: Yup.string(),
                    dsLinkThumbnail: Yup.string(),
                    dsTitulo: Yup.string(),
                    mimeType: Yup.string(),
                }),
                fgAtivo: Yup.boolean(),
                fgCapa: Yup.boolean(),
                fgExcluido: Yup.boolean(),
                fgNovo: Yup.boolean(),
            }),
        );
    };

    const validationSchema = useMemo(
        (): any =>
            Yup.object({
                dsBanner: Yup.string().required().max(32),
                dsLink: Yup.string(),
                nrOrdem: Yup.string().required(),
                tipoDispositivo: Yup.object({
                    idTipoDispositivo: Yup.number(),
                    dsTipoDispositivo: Yup.string(),
                }).required(),
                dtInicioVigencia: Yup.date().when('fgPermanente', {
                    is: true,
                    then: (schema) => schema.nullable(),
                    otherwise: (schema) =>
                        schema
                            .typeError('A data não é válida.')
                            .min(getMinDate(), 'Data de início de vigência deve ser maior ou igual a data atual.')
                            .required(),
                }),
                dtFimVigencia: Yup.date().when('fgPermanente', {
                    is: true,
                    then: (schema) => schema.nullable(),
                    otherwise: (schema) => schema.required().typeError('A data não é válida.'),
                }),
                bannerArquivos: imagePropsYupValidation().required().min(1),
            }),
        [],
    );

    const mapGetAvailableBannerModules = (getBannerModulos: any): any => {
        return getBannerModulos?.map((modulo: any) => {
            if (modulo?.modulo) {
                return { idModulo: modulo?.idModulo, dsModulo: modulo?.modulo?.dsModulo };
            }

            return;
        });
    };

    useEffect(() => {
        setLoading(isLoading);

        if (content && content?.bannerModulos.length) {
            content.bannerModulos = mapGetAvailableBannerModules(content?.bannerModulos);
        }
    }, [content, isLoading, setLoading]);

    const mapContentToInitialValues = useCallback((contentData: RequestBannerModelProps) => {
        if (content && contentData && contentData?.bannerModulos?.length) {
            contentData.bannerModulos = content?.bannerModulos;
        }

        return contentData;
    }, []);

    return (
        <FormPage
            title="Banner"
            values={initialBannerValues}
            onSubmit={handleSubmit}
            url={url}
            fgAtivo={true}
            validationSchema={validationSchema}
            goBackButton="/banner"
            mapContentToInitialValues={mapContentToInitialValues}
        >
            {(formik: any) => {
                return (
                    <React.Fragment>
                        <Block>
                            <Row>
                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        label="Descrição curta"
                                        name="dsBanner"
                                        value={formik.values.dsBanner}
                                        onChange={(e) => formik.setFieldValue('dsBanner', e.target.value)}
                                        error={formik.errors.dsBanner}
                                    />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        label="Link no Banner"
                                        name="dsLinkBanner"
                                        value={formik.values.dsLinkBanner}
                                        onChange={(e) => formik.setFieldValue('dsLinkBanner', e.target.value)}
                                        error={formik.errors.dsLink}
                                    />
                                </Grid>
                            </Row>

                            <Row>
                                <Grid sx={{ flex: 1 }}>
                                    <CissTextField
                                        label="Ordem do Banner"
                                        type="number"
                                        name="nrOrdem"
                                        value={formik.values.nrOrdem}
                                        onChange={(e) => formik.setFieldValue('nrOrdem', e.target.value)}
                                        error={formik.errors.nrOrdem}
                                    />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <Field
                                        as={FormControlLabel}
                                        type="checkbox"
                                        name="fgPermanente"
                                        control={<Checkbox />}
                                        label="Banner Permanente"
                                    />
                                </Grid>
                            </Row>

                            {!formik.values.fgPermanente && (
                                <Row>
                                    <Grid
                                        sx={{
                                            flex: 1,
                                        }}
                                    >
                                        <LocalizationProvider
                                            adapterLocale={ptBR}
                                            dateAdapter={AdapterDateFns}
                                            localeText={{ start: 'Início Vigência', end: 'Final Vigência' }}
                                        >
                                            <DateRangePicker
                                                value={[
                                                    formik.values.dtInicioVigencia ? new Date(formik.values.dtInicioVigencia) : null,
                                                    formik.values.dtFimVigencia ? new Date(formik.values.dtFimVigencia) : null,
                                                ]}
                                                onChange={([start, end]) => {
                                                    formik.setFieldValue('dtInicioVigencia', start);
                                                    formik.setFieldValue('dtFimVigencia', end);
                                                }}
                                                slots={{
                                                    field: MultiInputDateRangeField,
                                                }}
                                                slotProps={{
                                                    field: { dateSeparator: 'até' },
                                                    textField: ({ position }: FieldOwnerState & { position?: 'start' | 'end' }) => ({
                                                        name: position === 'start' ? 'dtInicioVigencia' : 'dtFimVigencia',
                                                        error:
                                                            position === 'start'
                                                                ? Boolean(formik.errors.dtInicioVigencia)
                                                                : Boolean(formik.errors.dtFimVigencia),
                                                        helperText:
                                                            position === 'start' ? formik.errors.dtInicioVigencia : formik.errors.dtFimVigencia,
                                                        autoComplete: 'off',
                                                    }),
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    <Grid
                                        sx={{
                                            flex: 1,
                                        }}
                                    />
                                </Row>
                            )}
                            <Row>
                                <Grid sx={{ flex: 1 }}>
                                    <Field component={DeviceFieldFormik} multiple={false} name="tipoDispositivo" />
                                </Grid>

                                <Grid sx={{ flex: 1 }}>
                                    <Field component={ModuleFieldFormik} multiple={true} name="bannerModulos" />
                                </Grid>
                            </Row>
                        </Block>
                        <Block>
                            <SubTitle
                                label={
                                    <React.Fragment>
                                        Banner
                                        <Tooltip title="Para inserir o banner, ele precisa atender a resolução 2560x466.">
                                            <InfoOutlinedIcon
                                                color="success"
                                                sx={{
                                                    ml: 2,
                                                    transform: 'translateY(3px)',
                                                }}
                                            />
                                        </Tooltip>
                                    </React.Fragment>
                                }
                            />

                            <Upload
                                id="bannerArquivos"
                                listFiles={formik.values.bannerArquivos}
                                setListFiles={(list: any) => formik.setFieldValue('bannerArquivos', list)}
                                fileLimit={1}
                                accept={uploadAcceptExtensions}
                                removeAll={false}
                                isBannerForm={true}
                            />

                            {formik.errors.bannerArquivos && (
                                <Typography
                                    sx={{
                                        color: (theme) => theme.palette.error.light,
                                    }}
                                >
                                    Insira ao menos um <strong>Arquivo Banner</strong> para criar um Banner.
                                </Typography>
                            )}
                        </Block>
                    </React.Fragment>
                );
            }}
        </FormPage>
    );
}
